/**
 * Remember: always add new reducers to the /reducers/index.js file!
 * Also, add your initialState to './initialstate'!
 */

import * as types from '../actions/actiontypes'; //just externalizes action name constants

import {HttpStatusCodes, MSGraphURL, documentTypes, DocumentSource} from '../util/uiconstants';
import {currentResource} from '../util/utils';
import initialState from './initialstate';
import Env from '../util/env';
import Utility from '../util/utilityfunctions';

/**
 * This reducer function handles all the actions related to the top-level objects.
 * @param {Object} state - A portion of the overall Redux state, specifically the
 *      store.<%=names.camel%> object, representing the current data.
 * @param {Object} action - Action object containing type and other values
 * @return {*} current state or array of language objects
 */
export default function errorReducer(state = initialState.errors, action) {
	switch (action.type) {
		case types.API_ERRORS:
			/* Notice that this is simply returning the action payload, <%=names.camel%>.
			 * This is because the combineReducers() call wraps this reducer
			 * and passes it only the portion of the store state that it
			 * is concerned with. */

			if (action.action?.avoidErrorSummary) {
				return state;
			}
			if (
				action?.error?.config?.headers.documentSourceid === DocumentSource.MobileDocUpload.toString() &&
				action?.error?.response?.status === 412
			) {
				return {
					message: currentResource.Errors[412],
					level: 'error'
				};
			}

			//Hiding error message notification when universal call fails, for nav.js purposes, and Archive
			if (action.action?.payload?.supressErrorOnUi || action?.supressErrorOnUi) {
				// This will check if there was any errors upon uploading a voice note.
				if (action?.error?.requestUrl?.indexOf('documents.json') > 0) {
					if (action?.action?.meta?.api?.headers?.DocumentTypeId === documentTypes.VOICE_RECORDING) {
						return {
							message: currentResource.labels.voiceNoteUploadError,
							level: 'error'
						};
					}
				}
				// don't show error on UI for e.g: we don't want to show error on UI if oversight and Iris api calls failed
				return state;
			}

			if (action.error.requestUrl?.indexOf('maxconnectionreached') > 0) {
				if (action.error?.response?.status === 403) {
					return {
						message: currentResource.Errors[34030],
						level: 'error'
					};
				} else {
					return {
						message: currentResource.labels.helixAnalyzerErrorMessage,
						level: 'error'
					};
				}
			}

			if (action.error.requestUrl?.indexOf('getglaccountstatus') > 0) {
				if (
					action.error?.response?.status === HttpStatusCodes.BadRequest ||
					action.error?.response?.status === HttpStatusCodes.NotFound
				) {
					if (action.error?.response?.data?.message && action.error?.response?.data?.code) {
						const errorMessage = action.error.response.data.message;
						const errorCode = action.error.response.data.code;
						const resouceLabel = currentResource.AnalyzerHelixErrors[errorCode] ?? '';
						if (resouceLabel) {
							return {
								message: errorCode + ': ' + resouceLabel,
								level: 'error'
							};
						} else if (errorMessage) {
							return {
								message: errorCode + ': ' + errorMessage,
								level: 'error'
							};
						}
					}
				}
				if (action.error?.response?.status === HttpStatusCodes.Forbidden) {
					return {
						message: action.error?.response?.status + ': ' + currentResource.Errors[34030],
						level: 'error'
					};
				} else {
					return {
						message: action.error?.response?.status
							? action.error?.response?.status + ': ' + currentResource.labels.helixAnalyzerGLAConnectionErrorMessage
							: currentResource.labels.helixAnalyzerGLAConnectionErrorMessage,
						level: 'error'
					};
				}
			}

			//If application gateway is rejecting input messages due to some suspicious special characters(forbidden by EY Information Security),
			if (action.error?.response?.status === 403 && action?.error?.response?.data?.errors === undefined) {
				return {
					message: currentResource.Errors[200001],
					level: 'error'
				};
			}

			if (action.error && action.error.requestUrl) {
				if (action.error.requestUrl?.indexOf('engagementmetadata.json') > 0) {
					return state;
				}

				if (action.error.requestUrl?.indexOf('workspaces.json') > 0) {
					return {
						message: currentResource.Errors[7014],
						level: 'error'
					};
				}

				if (action.error.requestUrl?.indexOf('HelixProjects') > 0) {
					if (action.error.requestUrl?.indexOf('api-version=2.0') > 0 && action.error.response.data.errors.Error) {
						let errorCodeStr = action.error.response.data.errors.Error[0];
						let errorCodeStrMatched = errorCodeStr.match(/^340\d{2}/);
						if (errorCodeStrMatched) {
							let errorCode = parseInt(errorCodeStrMatched[0]);
							if (errorCode >= 34001 && errorCode <= 34080) {
								return {
									message: currentResource.Errors[errorCode],
									level: 'error'
								};
							}
						}
					}
				}

				if (action?.error?.requestUrl?.indexOf('/Archives') > 0) {
					if (action.error.response.data) {
						action.error.response.status = null;
						action.error.response.statusText = null;
					}
				}

				if (action?.error?.requestUrl?.indexOf('/SendInstructions') > 0) {
					console.log(action);
					action.error = {};
					action.arguments = [];
				}

				if (
					action?.error?.requestUrl?.indexOf('IPFilters') > 0 &&
					action?.error?.response?.data?.errors.hasOwnProperty('Search')
				) {
					if (action?.error?.response?.data?.errors?.Search[0]?.includes('96005')) {
						return {
							message: currentResource.Errors[96005].replace('{0}', action?.action?.searchValue),
							level: 'error'
						};
					}
				}

				if (action?.error?.requestUrl?.indexOf('ClientRequests') > 0) {
					// This will suppress any errors thrown if the ClientGroup on an engagement has been disabled.
					if (action?.error?.response?.data?.length > 0) {
						const error = action?.error?.response?.data[0].errorMessage;
						if (action.action?.isAdvancedBulkOperations) {
							return error;
						} else if (error.includes('21032')) {
							return state;
						} else {
							let list = [];
							Object.entries(action.error.response.data).forEach((error) => {
								let code = error[1].errorMessage.split(' - ')[0];
								let message = error[1].errorMessage.split(' - ')[1];
								if (!list.some((item) => item.ErrorCode === code)) {
									list.push({
										ErrorCode: code,
										Message: message
									});
								}
							});
							return list;
						}
					} else if (action?.error?.response?.data) {
						let list = [];
						Object.entries(action?.error?.response?.data?.errors).forEach((error) => {
							if (error && Array.isArray(error)) {
								const key = Object.keys(error).find((key) => Array.isArray(error[key]));
								const messages = error[key];
								if (key && messages) {
									messages.forEach((msg) => {
										const [code, message] = msg.split(' - ');
										if (code && !list.some((item) => item.ErrorCode === code) && message) {
											list.push({ErrorCode: code, Message: message});
										}
									});
								}
							}
						});
						return list;
					}
				}
			}

			if (action.error.response && action.error.response.data) {
				if (action.error.response.data.Errors) {
					if (action.error.response.data.Errors.RBAC?.length === 1) {
						return action.error.response.data.Errors.RBAC[0];
					}
					if (
						_.size(action.error.response.data.Errors['RBACV2-Global']) > 0 &&
						action.error.response.data.Errors['RBACV2-Global'][0].includes('RBAC Authorization Denied') &&
						action.error.requestUrl.indexOf('/SchedulerRequests') > 0
					) {
						return currentResource.Errors['permissionIssueForScheduler'];
					}
					return action.error.response.data.Errors;
				}
				if (action.error.response.data.errors) {
					if (action.error.response.data.errors.Error) {
						return action.error.response.data.errors.Error[0];
					}
					if (Object.keys(action.error.response.data.errors).length > 0) {
						let list = [];
						Object.entries(action.error.response.data.errors).forEach((error) => {
							let code = error[1][0].split(' - ')[0];
							let message = error[1][0].split(' - ')[1];

							if (!list.some((item) => item.ErrorCode === code)) {
								list.push({
									ErrorCode: code,
									Message: message
								});
							}
						});

						return _.isEqual(list, state) ? state : list;
					}
				}
				// Handle doc service API error messages
				if (
					action.error.requestUrl?.startsWith(Env.getDocServiceUrl()) ||
					action.error.response?.request?.responseURL?.startsWith(Env.getDocServiceUrl())
				) {
					return handleDocServiceErrors(action) || state;
				}

				if (action.error.requestUrl?.startsWith(MSGraphURL.substr(0, 39))) {
					return state;
				}

				if (action.error.response.data.Message) {
					return action.error.response.data.Message;
				} else {
					return action.error.response.statusText;
				}
			} else {
				return action.error;
			}
		case types.CLIENT_VALIDATION_ERROR:
			return {
				message: action.message,
				level: 'error'
			};
		case types.SUCCESS_MESSAGE:
			return {
				message: action.message,
				level: 'success'
			};
		case types.WARNING_MESSAGE:
			return {
				message: action.message,
				level: 'warning'
			};
		case types.INFO_MESSAGE:
			return {
				message: action.message,
				level: 'info'
			};
		case types.CLEAR_ERRORS:
			return {};
		case types.CLOSE_CANVAS_FORM_COMBINED:
			return {};
		case 'UPCHUNK_UPLOAD_ERROR': {
			const errorObj1 = action.error;
			const documentSettingConfiguration = action?.documentSettingConfiguration;
			switch (errorObj1[0]) {
				case HttpStatusCodes.PreConditionFailed:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.PreConditionFailed]};
				case HttpStatusCodes.RequestEntityTooLarge:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.RequestEntityTooLarge]};
				case HttpStatusCodes.Unauthorized:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.Unauthorized]};
				case HttpStatusCodes.LengthRequired:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.LengthRequired]};
				case HttpStatusCodes.NotAcceptable:
					return {
						message: `${currentResource.Errors[errorObj1[0]]} '${errorObj1[1]}'`
					};
				case HttpStatusCodes.RequestUriTooLong:
					let message = `${currentResource.DocumentHelper[HttpStatusCodes.RequestUriTooLong]} '${errorObj1[1]}'`;
					if (documentSettingConfiguration) {
						message = message.replace('{0}', documentSettingConfiguration?.maxAllowedFileNameLength);
					}

					return {
						message: message
					};
				default:
					return {
						message: `${currentResource.Errors[errorObj1[2]]} '${errorObj1[1]}', ${currentResource.Errors[
							errorObj1[0]
						] || errorObj1[2]}`
					};
			}
		}

		case 'DOCHELPER_ERROR': {
			const errorObj = action.error;
			switch (errorObj[0]) {
				case HttpStatusCodes.PreConditionFailed:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.PreConditionFailed]};
				case HttpStatusCodes.RequestEntityTooLarge:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.RequestEntityTooLarge]};
				case HttpStatusCodes.Unauthorized:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.Unauthorized]};
				case HttpStatusCodes.NotAcceptable:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.NotAcceptable]};
				case HttpStatusCodes.Gone:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.Gone]};
				case HttpStatusCodes.LengthRequired:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.LengthRequired]};
				case HttpStatusCodes.RequestUriTooLong:
					let errorMessage = (currentResource.Errors[errorObj[0]] || errorObj[2])?.replace(
						'{0}',
						Utility.getMaxFileNameAllowedValue()
					);
					return {
						message: `[DocHelper Error][${errorObj[1]}]: ${errorMessage}`
					};
				default:
					return {
						message: `[DocHelper Error][${errorObj[1]}]: ${currentResource.Errors[errorObj[0]] || errorObj[2]}`
					};
			}
		}
		case 'INVALID_RELATED_ENTITY': {
			const errorObject = action.error;
			var entityError = '<strong>' + errorObject[0] + ':' + '</strong>';
			return {
				message: `${entityError} ${currentResource.Errors[errorObject[0]]}`
			};
		}

		default:
			return state;
	}
}

export function hideGlobalErrorToastReducer(state = initialState.isGlobalErrorToastHidden, action) {
	switch (action.type) {
		case types.SET_IS_GLOBAL_ERROR_TOAST_HIDDEN: {
			return action.isGlobalErrorToastHidden;
		}
		default:
			return state;
	}
}

//#region DocService functions

function handleDocServiceErrors(action) {
	const defaultErrorCode = 600;
	if (action.error.response && action.error.response.data) {
		const docServiceErrorMessage = action.error.response.data.ErrorMessage?.toLowerCase();
		const docServiceErrorCode = action.error.response.data.ErrorCode;

		if (docServiceErrorCode) {
			return createErrorItem(docServiceErrorCode);
		}
		if (docServiceErrorMessage) {
			if (docServiceErrorMessage.indexOf('Document with the same name exists') !== -1) {
				return createErrorItem(601);
			}
			if (docServiceErrorMessage.indexOf('Document is already in collaboration.') !== -1) {
				return createErrorItem(602);
			}
			if (docServiceErrorMessage.indexOf('document not found') !== -1) {
				return createErrorItem(603);
			}
			return createErrorItem(defaultErrorCode);
		}
	}
}

function createErrorItem(errorCode, errorLevel) {
	return {
		message: currentResource.Errors[errorCode],
		level: errorLevel || 'error'
	};
}

//#endregion
